import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Plus, ShoppingBag, Trash2 } from "lucide-react";

import {
  Box,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import DataTable from "@/components/mui/DataTable";

export default function Purchases() {
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  // Modal de recebimento
  const [receiveModal, setReceiveModal] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [receiveData, setReceiveData] = useState({ invoice_number: '', items: [] });
  const [receiving, setReceiving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [purchaseToDelete, setPurchaseToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Filtros
  const [filters, setFilters] = useState({
    supplier_id: '',
    status: '',
    date_from: '',
    date_to: '',
    invoice_number: '',
  });

  const [formData, setFormData] = useState({
    supplier_id: "",
    items: [],
  });

  const [newItem, setNewItem] = useState({
    product_id: "",
    quantity: 1,
    unit_price: 0,
  });

  const [pagination, setPagination] = useState({
    page: 1, // 1-based (Laravel)
    perPage: 15,
    total: 0,
    lastPage: 0,
  });

  useEffect(() => {
    fetchData(1, pagination.perPage);
  }, []);

  const fetchData = async (page = 1, perPage = pagination.perPage) => {
    try {
      setLoading(true);

      // Aplicar filtros na requisição
      const params = { page, per_page: perPage };
      if (filters.supplier_id) params.supplier_id = filters.supplier_id;
      if (filters.status) params.status = filters.status;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.invoice_number) params.invoice_number = filters.invoice_number;

      const [purchasesRes, suppliersRes, productsRes] = await Promise.all([
        axios.get(`${API}/purchases`, { params }),
        axios.get(`${API}/suppliers`, { params: { per_page: 1000 } }),
        axios.get(`${API}/products`, { params: { per_page: 1000 } }),
      ]);

      const purchasesData = purchasesRes.data;

      if (Array.isArray(purchasesData)) {
        setPurchases(purchasesData);
        setPagination({
          page: 1,
          perPage: purchasesData.length,
          total: purchasesData.length,
          lastPage: 1,
        });
      } else {
        setPurchases(purchasesData.data || []);
        setPagination({
          page: purchasesData.current_page,
          perPage: purchasesData.per_page,
          total: purchasesData.total,
          lastPage: purchasesData.last_page,
        });
      }

      setSuppliers(suppliersRes.data.data || suppliersRes.data);
      setProducts(productsRes.data.data || productsRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const addItemToPurchase = () => {
    if (!newItem.product_id) {
      toast.error("Selecione um produto");
      return;
    }

    const product = products.find(
      (p) => String(p.id) === String(newItem.product_id)
    );
    if (!product) return;

    const quantity = Number(newItem.quantity) || 0;
    const unitPrice = Number(newItem.unit_price) || 0;

    const item = {
      product_id: newItem.product_id,
      product_name: product.name,
      quantity,
      unit_price: unitPrice,
      total: quantity * unitPrice,
    };

    setFormData((prev) => ({ ...prev, items: [...prev.items, item] }));
    setNewItem({ product_id: "", quantity: 1, unit_price: 0 });
  };

  const removeItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) {
      toast.error("Adicione pelo menos um item");
      return;
    }

    try {
      await axios.post(`${API}/purchases`, formData);
      toast.success("Pedido de compra criado!");
      setShowModal(false);
      resetForm();
      fetchData(pagination.page, pagination.perPage);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar pedido");
    }
  };

  const updateStatus = async (purchaseId, status) => {
    // Se o status for "received", abrir o modal de confirmação
    if (status === 'received') {
      openReceiveModal(purchaseId);
      return;
    }
    
    try {
      await axios.put(
        `${API}/purchases/${purchaseId}/status?status=${status}`
      );
      toast.success("Status atualizado!");
      fetchData(pagination.page, pagination.perPage);
    } catch (error) {
      toast.error("Erro ao atualizar status");
    }
  };

  const openDeleteDialog = (purchase) => {
    setPurchaseToDelete(purchase);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleting) {
      return;
    }

    setDeleteDialogOpen(false);
    setPurchaseToDelete(null);
  };

  const deletePurchase = async () => {
    if (!purchaseToDelete) {
      return;
    }

    try {
      setDeleting(true);
      await axios.delete(`${API}/purchases/${purchaseToDelete.id}`);
      toast.success("Pedido de compra removido!");
      closeDeleteDialog();
      fetchData(pagination.page, pagination.perPage);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Erro ao remover pedido de compra"
      );
    } finally {
      setDeleting(false);
    }
  };

  const openReceiveModal = async (purchaseId) => {
    try {
      const res = await axios.get(`${API}/purchases/${purchaseId}`);
      setSelectedPurchase(res.data);
      setReceiveData({
        invoice_number: res.data.invoice_number || '',
        items: res.data.items?.map(item => ({
          id: item.id,
          product_name: item.product?.name || 'Produto',
          quantity: item.quantity,
          unit_price: item.unit_price,
        })) || [],
      });
      setReceiveModal(true);
    } catch (error) {
      toast.error("Erro ao carregar dados da compra");
    }
  };

  const handleReceiveItemChange = (itemId, field, value) => {
    setReceiveData(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleConfirmReceive = async () => {
    if (!receiveData.invoice_number) {
      toast.error("Informe o número da nota fiscal");
      return;
    }

    try {
      setReceiving(true);
      await axios.post(`${API}/purchases/${selectedPurchase.id}/confirm-receive`, {
        invoice_number: receiveData.invoice_number,
        items: receiveData.items.map(item => ({
          id: item.id,
          quantity: parseInt(item.quantity) || 0,
          unit_price: parseFloat(item.unit_price) || 0,
        })),
      });
      toast.success("Compra recebida! Estoque atualizado.");
      setReceiveModal(false);
      setSelectedPurchase(null);
      fetchData(pagination.page, pagination.perPage);
    } catch (error) {
      toast.error(error.response?.data?.message || "Erro ao confirmar recebimento");
    } finally {
      setReceiving(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    fetchData(1, pagination.perPage);
  };

  const clearFilters = () => {
    setFilters({ supplier_id: '', status: '', date_from: '', date_to: '', invoice_number: '' });
    fetchData(1, pagination.perPage);
  };

  const resetForm = () => {
    setFormData({ supplier_id: "", items: [] });
    setNewItem({ product_id: "", quantity: 1, unit_price: 0 });
  };

  const getStatusChipProps = (status) => {
    switch (status) {
      case "open":
        return { label: "Pendente", color: "warning" };
      case "partial":
        return { label: "Parcial", color: "info" };
      case "received":
        return { label: "Recebido", color: "success" };
      default:
        return { label: status, color: "default" };
    }
  };

  const formatMoney = (value) =>
    `R$ ${Number(value || 0).toFixed(2).replace(".", ",")}`;

  // paginação (TablePagination usa página 0-based)
  const handleChangePage = (event, newPageZeroBased) => {
    const newPage = newPageZeroBased + 1;
    fetchData(newPage, pagination.perPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newPerPage = parseInt(event.target.value, 10);
    setPagination((prev) => ({
      ...prev,
      perPage: newPerPage,
      page: 1,
    }));
    fetchData(1, newPerPage);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const totalPedido = formData.items.reduce(
    (sum, item) => sum + item.total,
    0
  );

  const selectedProduct =
    products.find(
      (p) => String(p.id) === String(newItem.product_id)
    ) || null;

  // ======= COLUNAS TABELA PRINCIPAL (PEDIDOS) =======
  const purchaseColumns = [
    {
      id: "created_at",
      label: "Data",
      render: (purchase) =>
        new Date(purchase.created_at).toLocaleString("pt-BR"),
    },
    {
      id: "supplier",
      label: "Fornecedor",
      render: (purchase) => (
        <Typography fontWeight={600}>
          {purchase.supplier?.name}
        </Typography>
      ),
    },
    {
      id: "items",
      label: "Itens",
      align: "center",
      render: (purchase) => purchase.items?.length ?? 0,
    },
    {
      id: "total",
      label: "Total",
      align: "left",
      render: (purchase) => (
        <Typography fontWeight={700}>
          {formatMoney(purchase.total)}
        </Typography>
      ),
    },
    {
      id: "status",
      label: "Status",
      render: (purchase) => {
        const chipProps = getStatusChipProps(purchase.status);
        return (
          <Chip
            size="small"
            color={chipProps.color}
            label={chipProps.label}
          />
        );
      },
    },
    {
      id: "actions",
      label: "Ações",
      render: (purchase) => (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {purchase.status !== "received" && (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={purchase.status}
                onChange={(e) => updateStatus(purchase.id, e.target.value)}
                data-testid={`purchase-status-${purchase.id}`}
              >
                <MenuItem value="open">Pendente</MenuItem>
                <MenuItem value="partial">Parcial</MenuItem>
                <MenuItem value="received">Recebido</MenuItem>
              </Select>
            </FormControl>
          )}

          {!purchase.stock_received && (
            <Button
              type="button"
              variant="outlined"
              color="error"
              size="small"
              startIcon={<Trash2 size={14} />}
              onClick={() => openDeleteDialog(purchase)}
              data-testid={`delete-purchase-${purchase.id}`}
            >
              Remover
            </Button>
          )}
        </Box>
      ),
    },
  ];

  const purchasePagination = {
    page: pagination.page - 1, // 0-based para o MUI
    rowsPerPage: pagination.perPage,
    total: pagination.total,
    onPageChange: handleChangePage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: [5, 10, 15, 25, 50],
  };

  // ======= COLUNAS TABELA ITENS DO PEDIDO (DIALOG) =======
  const purchaseItemColumns = [
    { id: "product_name", label: "Produto" },
    { id: "quantity", label: "Qtd", align: "center" },
    {
      id: "unit_price",
      label: "Preço Unit.",
      align: "right",
      render: (row) => formatMoney(row.unit_price),
    },
    {
      id: "total",
      label: "Total",
      align: "right",
      render: (row) => (
        <Typography fontWeight={700}>
          {formatMoney(row.total)}
        </Typography>
      ),
    },
    {
      id: "actions",
      label: "",
      align: "right",
      render: (row) => (
        <Button
          type="button"
          variant="outlined"
          color="error"
          size="small"
          onClick={() => removeItem(row._index)}
          data-testid={`remove-purchase-item-${row._index}`}
        >
          ×
        </Button>
      ),
    },
  ];

  const purchaseItemRows = formData.items.map((item, index) => ({
    ...item,
    _index: index, // usado para remover
  }));

  return (
    <Box data-testid="purchases-page">
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Pedidos de Compra
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Lista de registros de compras a serem feitos
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          data-testid="add-purchase-btn"
        >
          Novo Pedido
        </Button>
      </Box>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>Filtros</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Fornecedor</InputLabel>
            <Select
              value={filters.supplier_id}
              label="Fornecedor"
              onChange={(e) => handleFilterChange('supplier_id', e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              {suppliers.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="open">Pendente</MenuItem>
              <MenuItem value="partial">Parcial</MenuItem>
              <MenuItem value="received">Recebido</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Nº Nota Fiscal"
            value={filters.invoice_number}
            onChange={(e) => handleFilterChange('invoice_number', e.target.value)}
            sx={{ width: 150 }}
          />

          <TextField
            size="small"
            type="date"
            label="De"
            value={filters.date_from}
            onChange={(e) => handleFilterChange('date_from', e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 140 }}
          />

          <TextField
            size="small"
            type="date"
            label="Até"
            value={filters.date_to}
            onChange={(e) => handleFilterChange('date_to', e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 140 }}
          />

          <Button variant="contained" size="small" onClick={applyFilters}>Filtrar</Button>
          <Button variant="outlined" size="small" onClick={clearFilters}>Limpar</Button>
        </Box>
      </Paper>

      <Paper className="data-table-container">
        {/* Tabela / vazio */}
        {pagination.total === 0 ? (
          <Box
            sx={{
              py: 6,
              px: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 1.5,
            }}
          >
            <ShoppingBag size={64} />
            <Typography variant="h6">Nenhum pedido de compra</Typography>
            <Typography variant="body2">
              Crie pedidos de compra para seus fornecedores
            </Typography>
          </Box>
        ) : (
          <DataTable
            columns={purchaseColumns}
            rows={purchases}
            getRowId={(row) => row.id}
            getRowTestId={(row) => `purchase-row-${row.id}`}
            pagination={purchasePagination}
          />
        )}
      </Paper>

      {/* Dialog Novo Pedido */}
      <Dialog
        open={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Novo Pedido de Compra</DialogTitle>
        <DialogContent dividers>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            {/* Fornecedor */}
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="purchase-supplier-label">
                  Fornecedor *
                </InputLabel>
                <Select
                  labelId="purchase-supplier-label"
                  label="Fornecedor"
                  value={formData.supplier_id}
                  onChange={(e) =>
                    setFormData({ ...formData, supplier_id: e.target.value })
                  }
                  required
                  data-testid="purchase-supplier-select"
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {suppliers.map((supplier) => (
                    <MenuItem key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Card Adicionar Item */}
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 2,
                mb: 2,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, mb: 1.5 }}
              >
                Adicionar Item
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 1fr auto",
                  gap: 1.5,
                  alignItems: "flex-end",
                }}
              >
                {/* Produto - Autocomplete */}
                <Autocomplete
                  options={products}
                  value={selectedProduct}
                  onChange={(event, value) =>
                    setNewItem({
                      ...newItem,
                      product_id: value?.id || "",
                    })
                  }
                  getOptionLabel={(option) =>
                    option?.name
                      ? `${option.name}${option.barcode ? ` (cód: ${option.barcode})` : ""
                      }`
                      : ""
                  }
                  filterOptions={(options, state) =>
                    options.filter((p) => {
                      const term = state.inputValue.toLowerCase();
                      return (
                        p.name.toLowerCase().includes(term) ||
                        (p.barcode &&
                          String(p.barcode)
                            .toLowerCase()
                            .includes(term))
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Produto"
                      size="small"
                      fullWidth
                      data-testid="purchase-item-product-select"
                    />
                  )}
                />

                {/* Quantidade */}
                <TextField
                  label="Qtd"
                  type="number"
                  size="small"
                  fullWidth
                  value={newItem.quantity}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      quantity: parseInt(e.target.value, 10) || 0,
                    })
                  }
                  data-testid="purchase-item-quantity-input"
                />

                {/* Preço */}
                <TextField
                  label="Preço"
                  type="number"
                  size="small"
                  fullWidth
                  inputProps={{ step: "0.01" }}
                  value={newItem.unit_price}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      unit_price: parseFloat(e.target.value) || 0,
                    })
                  }
                  data-testid="purchase-item-price-input"
                />

                {/* Botão adicionar */}
                <Button
                  type="button"
                  variant="contained"
                  onClick={addItemToPurchase}
                  data-testid="add-purchase-item-btn"
                >
                  <Plus size={16} />
                </Button>
              </Box>
            </Box>

            {/* Itens do Pedido */}
            {formData.items.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 1 }}
                >
                  Itens do Pedido
                </Typography>

                <DataTable
                  columns={purchaseItemColumns}
                  rows={purchaseItemRows}
                  getRowId={(row) => row._index}
                  getRowTestId={(row) => `purchase-item-row-${row._index}`}
                  containerSx={{ p: 0, pb: 0 }}
                />

                <Box
                  sx={{
                    textAlign: "right",
                    mt: 1.5,
                    fontSize: 18,
                    fontWeight: 700,
                  }}
                >
                  Total: {formatMoney(totalPedido)}
                </Box>
              </Box>
            )}

            <DialogActions sx={{ mt: 2 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                data-testid="cancel-purchase-btn"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                data-testid="submit-purchase-btn"
              >
                Criar Pedido
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Excluir Pedido de Compra</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Confirma a exclusão do pedido de compra
            {purchaseToDelete ? ` #${purchaseToDelete.id}` : ""}?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta ação não poderá ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleting}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={deletePurchase}
            disabled={deleting}
          >
            {deleting ? "Excluindo..." : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Confirmação de Recebimento */}
      <Dialog open={receiveModal} onClose={() => setReceiveModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Confirmar Recebimento - Compra #{selectedPurchase?.id}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Revise os itens e informe o número da nota fiscal antes de confirmar.
            O estoque será atualizado automaticamente.
          </Typography>

          <TextField
            fullWidth
            label="Número da Nota Fiscal *"
            value={receiveData.invoice_number}
            onChange={(e) => setReceiveData(prev => ({ ...prev, invoice_number: e.target.value }))}
            sx={{ mb: 3 }}
            data-testid="receive-invoice-number"
          />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Itens para dar entrada no estoque:</Typography>
          
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {receiveData.items.map((item, index) => (
              <Paper key={item.id} sx={{ p: 2, mb: 1, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography sx={{ flex: 2 }}>{item.product_name}</Typography>
                <TextField
                  label="Qtd"
                  type="number"
                  size="small"
                  value={item.quantity}
                  onChange={(e) => handleReceiveItemChange(item.id, 'quantity', e.target.value)}
                  sx={{ width: 100 }}
                  data-testid={`receive-qty-${index}`}
                />
                <TextField
                  label="Custo Unit."
                  type="number"
                  size="small"
                  inputProps={{ step: "0.01" }}
                  value={item.unit_price}
                  onChange={(e) => handleReceiveItemChange(item.id, 'unit_price', e.target.value)}
                  sx={{ width: 120 }}
                  data-testid={`receive-price-${index}`}
                />
                <Typography sx={{ width: 100, textAlign: 'right', fontWeight: 600 }}>
                  {formatMoney((item.quantity || 0) * (item.unit_price || 0))}
                </Typography>
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setReceiveModal(false)} color="inherit">
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmReceive}
            disabled={receiving}
            data-testid="confirm-receive-btn"
          >
            {receiving ? <CircularProgress size={20} /> : "Confirmar Recebimento"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
